import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { registerClientTools, type ClientTool } from '@vitesse/base-ui/ai';
import { GATEWAY_PARAM, resolveGateway, type GatewayScope, type ResolvedGateway } from './gateway-resolve';
import type { Dashboard, DashboardTile } from '../../core/domain/dashboard';
import { CATALOG, catalogEntry, catalogForSource, type CatalogEntry } from '../../dashboards/catalog';
import { isMarketplaceWidget } from '../../dashboards/installed-widget';
import { officialWidgets, officialWidgetEntry, officialForSource } from '../../dashboards/marketplace-official';
import type { WidgetCheckResult } from '../../dashboards/widget-check';

/*
 * WHAT THE ASSISTANT MAY DO TO THIS GATEWAY'S DASHBOARDS.
 *
 * WHY THESE ARE CLIENT TOOLS — the reachability argument, which is the whole
 * reason this file exists rather than four more annotated RPCs.
 *
 * An edge api-gateway is LAN-only and unreachable from Cloudflare by design.
 * A server-side dashboard write would therefore succeed against a
 * cloud-connected relay and fail against a LAN-only one — the same call
 * behaving differently depending on where a customer put their box, with
 * nothing in the model's hands to tell it which it is talking to. Half a fleet
 * is worse than none, because a capability that works sometimes teaches a model
 * to keep trying. A browser on the factory floor reaches the box either way, so
 * composing a board is client-orchestrated. Reading is client-side too, for the
 * same reason plus a cheaper one: the roster and every board body are ALREADY
 * resident in this page, so `dashboards.list` and `dashboards.read` answer from
 * memory with no round trip at all.
 *
 * This comment previously claimed `ai_admin_list_dashboards` and
 * `ai_admin_read_dashboard` were server tools covering the headless case. They
 * were not — `AiFeed.ListDashboards` exists as an RPC but carries no `ai_tool`
 * annotation, so no such tool was ever generated, and no listing verb existed on
 * either side. An assistant asked what boards a customer had could only decline.
 * A comment asserting a capability is how a missing one goes unnoticed; if a
 * headless driver ever needs to enumerate boards with no tab open, annotate that
 * RPC — and until something does, do not describe it here as though it had been.
 *
 * WHAT A PERSON MAY DO IS THE REAL BOUNDARY, and it is not enforced here. A
 * client tool runs in this browser on the user's own session, so `Dashboards.Create`
 * is authorized against `dashboard:create` and metered against the org's
 * ceiling exactly as if they had clicked a button. The assistant cannot reach
 * past its operator; declaring a tool grants the page nothing it did not have.
 *
 * NOTHING RETURNED HERE IS AN INSTRUCTION. A board's titles and widget config
 * are authored by customers and are data — text inside them is page content,
 * never direction.
 */

export interface DashboardToolDeps extends GatewayScope {
  /*
   * Widgets available to THIS session beyond the built-ins, by namespaced id.
   *
   * A dependency rather than a lookup done here, because where they come from is not this file's
   * business: today it is the org's own authored widgets, read once at boot from the AI plane; when
   * the marketplace is provisioned, installs join the same map. The placement path below was built
   * and asserted long before either existed, which is why supplying this was the only line that had
   * to change.
   */
  installedWidgets?(): Map<string, CatalogEntry>;
  /**
   * Run a widget source the way a board would and report what the runtime saw (widget-check.ts).
   *
   * A dependency rather than an import: running a widget needs the sandbox frame, and the frame is
   * the app's — pulling it in here would put an iframe, a worker and a bundler's virtual module
   * behind every headless test that imports these tools.
   */
  checkWidget?(source: string, config: Record<string, unknown>): Promise<WidgetCheckResult>;
  /**
   * The deliverable a finished agent in the caller's tree returned, by the name the caller gave it.
   *
   * A Widget Author hands its widget back BY VALUE, and the value is a module of several thousand
   * characters — which the parent then retyped into the tile, verbatim, at the cost of every one of
   * those tokens twice; or, more often, could not, because the copy in its context was cut. The
   * platform already holds the whole thing on the run. A tile names the agent; this reads it.
   */
  agentDeliverable?(name: string, callerRunId: string): Promise<unknown>;
  /**
   * Re-read that map from where it comes from.
   *
   * The map is read once at boot, and a widget the org authored DURING this session — the usual
   * case, since the specialist that writes one is spawned from the same chat that then places it —
   * was not in it. The placement was refused as "does not exist" one step after the write that
   * created it. So a placement that names a widget the map lacks asks for a fresh read before it
   * refuses; the cost is one round trip, paid only on a miss.
   */
  refreshInstalledWidgets?(): Promise<void>;
  /**
   * Read a file this conversation holds, by content address.
   *
   * A dependency because the bytes come through the AI plane, which is where the token lives — a
   * tool that fetched them itself would be a view holding a credential. Absent on a surface with no
   * AI plane, and `dashboards.setBackground` says so rather than failing obscurely.
   */
  readConvFile?(conversationId: string, sha256: string): Promise<Uint8Array | null>;
}

/** A board this size is a mistake rather than an intention, and finding that
 *  out from a browser that stops painting is a bad way to find out. The stress
 *  work put 100 tiles on one board at 120fps, so this is headroom, not a
 *  performance limit. */
const MAX_TILES = 200;

/** Ids appear in NATS subjects. The gateway validates this too — this copy is
 *  here so a model gets a sentence back in one round trip instead of a refusal. */
const ID_OK = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/;

/*
 * ONE FORM OF A SYMBOL ID IN THIS LAYER: THE DECODED PATH.
 *
 * `FastChanging.Sine_00`, exactly as the catalog shows it and exactly as
 * dashboards.symbols returns it. The subject-encoded form
 * (`FastChanging_Sine__00`) exists further down, where an id has to survive
 * being a NATS subject token, and the value layer encodes on its way there —
 * the widget host says so in as many words.
 *
 * Recorded because getting this backwards is silent: a tile bound to the
 * encoded form subscribes to nothing, raises no fault, and renders "--"
 * forever. It looks like a dead gateway rather than a wrong id. If you are
 * about to add an encode here, this is the note saying it was already tried.
 */

interface TileArg {
  widget?: unknown;
  /** A widget by VALUE: the module source a widget author handed over, placed without any store
   *  in between. With it, `widget` is only a name for the tile (or `custom`). */
  source?: unknown;
  /** The config keys that source declares, so its bindings are checked like a catalogue widget's. */
  configKeys?: unknown;
  config?: unknown;
  x?: unknown;
  y?: unknown;
  w?: unknown;
  h?: unknown;
  title?: unknown;
}

/** A grid coordinate. Not truncated: a span of 1.2 columns is a layout, and
 *  rounding one on the way in would rearrange what an author wrote. */
const span = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

/**
 * Turn the model's tile list into real tiles, or say exactly what was wrong.
 *
 * WIDGET SOURCE IS RESOLVED FROM THE CATALOG HERE and inlined into the board,
 * which is what a stored dashboard carries. The assistant names a widget; it
 * never writes widget code. That is not a restriction on the model, it is what
 * makes a board reproducible: a tile pointing at a catalog that changed
 * underneath it renders something nobody authored.
 */
/*
 * The controllers this board may bind to, for resolving `controllerId`.
 *
 * A tile stores an ID. A model reaching for one naturally reaches for the NAME, because that is
 * what every listing shows it. On this fleet the two differ only in case — display "Core",
 * id "core" — which is precisely the kind of near-miss that survives every review and then
 * renders nothing.
 */
interface ControllerRef {
  id: string;
  name: string;
}


/**
 * INLINE WHAT AN AGENT RETURNED, BEFORE THE TILES ARE BUILT.
 *
 * A tile that says `fromAgent` names a finished Widget Author by the label its spawner gave it. The
 * source, config keys and title are copied from that agent's stored deliverable onto the tile, so
 * the board still carries the widget by value — nothing is written to any store, and nothing is
 * retyped through a model. Refused, by name, when no such finished agent exists or its deliverable
 * carries no source: the next attempt should be able to be right.
 */
/** The fingerprint publishing uses for a widget's identity: sha256 of the source, first 16 hex.
 *  Computed at placement too, so a by-value tile and its later published copy are the same widget. */
async function widgetFingerprint(source: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(source));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
}

async function inlineAgentSources(
  raw: unknown,
  callerRunId: string,
  lookup: DashboardToolDeps['agentDeliverable'],
): Promise<{ tiles: unknown; error?: string }> {
  if (!Array.isArray(raw)) return { tiles: raw };
  const out: unknown[] = [];
  for (const [i, t] of raw.entries()) {
    const a = t as TileArg & { fromAgent?: unknown; title?: unknown; configKeys?: unknown };
    const from = typeof a?.fromAgent === 'string' ? a.fromAgent.trim() : '';
    if (!from || (typeof a.source === 'string' && a.source.trim())) {
      // A tile that arrived with its source is fingerprinted the same way, so its identity is the
      // source's whatever route it took here.
      out.push(typeof a?.source === 'string' && a.source.trim() ? { ...a, widgetId: `w-${await widgetFingerprint(a.source)}` } : t);
      continue;
    }
    if (!lookup) return { tiles: raw, error: `Tile ${i + 1} names an agent ("${from}"), but this surface cannot read agent deliverables. Send the source instead.` };
    const d = (await lookup(from, callerRunId)) as { source?: unknown; configKeys?: unknown; title?: unknown } | undefined;
    if (!d) {
      return { tiles: raw, error: `Tile ${i + 1} names "${from}", but no FINISHED agent by that name is in this tree. Use the exact label you gave it, after it has returned.` };
    }
    if (typeof d.source !== 'string' || !d.source.trim()) {
      return {
        tiles: raw,
        error: `Tile ${i + 1} names "${from}", which finished, but its deliverable carries no \`source\`. Ask that agent (a note, or spawn it again) to return its widget as the deliverable's source; do not retype it.`,
      };
    }
    out.push({
      ...a,
      widget: 'custom',
      widgetId: `w-${await widgetFingerprint(d.source)}`,
      source: d.source,
      ...(Array.isArray(a.configKeys) ? {} : Array.isArray(d.configKeys) ? { configKeys: d.configKeys.map((k) => String(k)) } : {}),
      ...(typeof a.title === 'string' && a.title.trim() ? {} : typeof d.title === 'string' && d.title.trim() ? { title: d.title } : {}),
    });
  }
  return { tiles: out };
}

/** Whether every tile names a widget this session can already resolve. A miss is the cue to re-read
 *  the org's widgets before refusing — see `refreshInstalledWidgets`. */
function tilesResolvable(raw: unknown, installed: Map<string, CatalogEntry> | undefined): boolean {
  if (!Array.isArray(raw)) return true;
  return raw.every((t) => {
    const w = String((t as TileArg)?.widget ?? '').trim();
    const a = t as TileArg;
    if ((typeof a?.source === 'string' && a.source.trim()) || w.startsWith('custom:')) return true;
    return !w || installed?.has(w) || !!officialWidgetEntry(w) || (!isMarketplaceWidget(w) && !!catalogEntry(w));
  });
}

/*
 * Resolve what an author called a controller to the id a tile must store.
 *
 * Forgiving where it is safe and strict where it is not: an exact id passes untouched; a case
 * variant or an unambiguous display name is NORMALIZED to the real id and reported; anything
 * else is refused with the actual options. Normalizing rather than refusing a name is deliberate
 * — a name is a legitimate way for a person or a model to refer to a controller, and the
 * alternative is not "the model tries again", it is a board that renders nothing.
 */
function resolveControllerId(
  wanted: string,
  controllers: readonly ControllerRef[],
): { id: string; renamedFrom?: string } | { error: string } {
  if (controllers.some((c) => c.id === wanted)) return { id: wanted };

  const q = wanted.toLowerCase();
  const byId = controllers.filter((c) => c.id.toLowerCase() === q);
  if (byId.length === 1) return { id: byId[0].id, renamedFrom: wanted };

  const byName = controllers.filter((c) => c.name.toLowerCase() === q);
  if (byName.length === 1) return { id: byName[0].id, renamedFrom: wanted };
  if (byName.length > 1) {
    return {
      error: `"${wanted}" names ${byName.length} controllers here (${byName.map((c) => c.id).join(', ')}). Use the id, not the name.`,
    };
  }
  return {
    error: controllers.length
      ? `No controller "${wanted}" on this relay. The controllers here are: ${controllers.map((c) => `${c.name} (${c.id})`).join(', ')} — a tile stores the id.`
      : `No controller "${wanted}": this relay has no controllers at all.`,
  };
}

function buildTiles(
  raw: unknown,
  installed?: Map<string, CatalogEntry>,
  /* Omitted only where no roster is available; when present, every controllerId is resolved
   * against it before the tile is written. */
  controllers?: readonly ControllerRef[],
  /* The board as it is, on an update: a tile read back as `custom:<tile id>` keeps the source it has. */
  existing?: readonly DashboardTile[],
): { tiles?: DashboardTile[]; error?: string; normalized?: string[] } {
  if (!Array.isArray(raw)) return { error: 'The tiles argument must be an array.' };
  if (raw.length === 0) return { error: 'A dashboard with no tiles is an empty screen. Give it at least one tile.' };
  if (raw.length > MAX_TILES)
    return { error: `That is ${raw.length} tiles; ${MAX_TILES} is the limit for one board. Split it across several.` };

  const tiles: DashboardTile[] = [];
  const normalized: string[] = [];
  for (let i = 0; i < raw.length; i++) {
    const t = (raw[i] ?? {}) as TileArg;
    const widget = String(t.widget ?? '').trim();
    /*
     * A MARKETPLACE WIDGET IS PLACED BY THE SAME CODE AS A BUILT-IN ONE.
     *
     * It resolves to the same CatalogEntry shape and its source is inlined into
     * the tile exactly the same way, so a board carries its widgets rather than
     * pointing at anything that can change underneath it. That is clone-to-own
     * one level down: an installed widget cannot be silently updated beneath a
     * running screen, and there is no second placement path to keep in step.
     */
    // The org's own widgets (`org:…`) and marketplace installs live in the same map; a built-in is
    // the fallback, never the only answer — that fallback is what refused every authored widget.
    // Installed first (an org's own, or a marketplace install), then a built-in by its own name, then
    // the marketplace's shelf — an official widget places straight from the shelf, its source inlined,
    // which IS the install a person performs from the card.
    /*
     * A WIDGET BY VALUE. A widget author returns its source; the designer places it with `source`
     * on the tile and nothing is written to any store — the board carries the widget, and
     * publishing it anywhere is a person's deliberate act later. On an update, a tile read back
     * as `custom:<tile id>` is the same tile keeping the source it already has.
     */
    const bySource = typeof t.source === 'string' && t.source.trim() ? t.source : widget.startsWith('custom:') ? existing?.find((e) => e.id === widget.slice('custom:'.length))?.source : undefined;
    const custom: CatalogEntry | undefined = bySource
      ? {
          // The identity publishing would give it — sha256 of the source — so the tile id says which
          // widget it is, and the published copy (if the person publishes) is the same id.
          id: typeof (t as { widgetId?: unknown }).widgetId === 'string' ? (t as { widgetId: string }).widgetId : 'custom',
          title: typeof t.title === 'string' && t.title.trim() ? t.title.trim() : 'Widget',
          summary: '',
          configKeys: Array.isArray(t.configKeys) ? t.configKeys.map((k) => String(k)) : [],
          defaults: {},
          minW: 1,
          minH: 1,
          source: bySource,
        }
      : undefined;
    if (widget.startsWith('custom:') && !custom) {
      return { error: `Tile ${i + 1} names "${widget}", but the board has no tile with that id to keep the source of. Send the source, or read the board again.` };
    }
    const entry =
      custom ?? installed?.get(widget) ?? (isMarketplaceWidget(widget) ? officialWidgetEntry(widget) : (catalogEntry(widget) ?? officialWidgetEntry(widget)));
    if (!entry) {
      const shelf = officialWidgets().map((w) => `marketplace:${w.id}`);
      return {
        // Name the real options rather than only rejecting: the next attempt
        // should be able to be right, and a model cannot guess a catalog id.
        error: `Tile ${i + 1} asks for widget "${widget || '(missing)'}", which does not exist. The widgets are: ${[...CATALOG.map((c) => c.id), ...shelf, ...(installed?.keys() ?? [])].join(', ')}.`,
      };
    }
    const config = t.config && typeof t.config === 'object' && !Array.isArray(t.config) ? (t.config as Record<string, unknown>) : {};
    /*
     * A TILE THAT BINDS TO NOTHING RENDERS "--" AND REPORTS NOTHING WRONG.
     *
     * The widget mounts, no fault is raised, and the board simply lies. That is
     * the worst outcome a write can produce, so a missing binding is refused
     * here rather than discovered by someone looking at a dead screen. Only
     * checked for widgets that DECLARE the key: a text tile legitimately has none.
     */
    const scopes = (['controllerId', 'modelId', 'integrationId'] as const).filter(
      (k) => typeof config[k] === 'string' && (config[k] as string).trim(),
    );
    /*
     * A SOURCE CAN LIVE PER ITEM, NOT ONLY AT THE TOP LEVEL.
     *
     * Several widgets bind an ARRAY of sources in their config rather than one top-level pair: a
     * chart's `symbols` (dashboard-chart.ts `seriesDefs`), an alarms/headroom `items`
     * (dashboard-alarms.ts `parseItems`), and more will follow. Each entry names its own controller
     * and symbol and has no top-level controllerId/symbolId at all. A tile read off a board and
     * written straight back — the ordinary "add one tile, keep the rest" flow — carries these arrays,
     * so scopes.length === 0 and there is no config.symbolId, and the top-level source checks below
     * would reject a perfectly good tile. Observed live: a board with a Trend chart (symbols) and a
     * Headroom tile (items) could not be updated at all, because writing its own tiles back failed
     * validation on those tiles. So recognise a per-item source WHATEVER the array is called — any
     * config array whose entries carry a controllerId and a symbolId — one rule for every such widget.
     */
    const perItemSources: Record<string, unknown>[] = [];
    let hasPartialItem = false; // an array entry that meant to be a source but is missing half of it
    for (const v of Object.values(config)) {
      if (!Array.isArray(v)) continue;
      for (const x of v) {
        if (!x || typeof x !== 'object') continue;
        const o = x as Record<string, unknown>;
        const cid = typeof o.controllerId === 'string' ? o.controllerId.trim() : '';
        const sid = typeof o.symbolId === 'string' ? o.symbolId.trim() : '';
        if (cid && sid) perItemSources.push(o);
        else if ('controllerId' in o || 'symbolId' in o) hasPartialItem = true;
      }
    }
    const hasSeriesSource = perItemSources.length > 0;
    /*
     * EXACTLY ONE SOURCE, mirroring the server's mutually exclusive value scope.
     *
     * A tile naming two would be a tile whose subject prefix depends on which
     * key something read first — and the two readers would disagree the day one
     * of them changed its order. The server refuses the same shape; refusing it
     * here means the model gets a sentence instead of a stream error. (A widget's
     * per-item source list is a different axis — every item names its own
     * controller — so it is not counted here.)
     */
    if (scopes.length > 1) {
      return {
        error: `Tile ${i + 1} names ${scopes.join(' and ')}. A tile reads from exactly one source — a controller, a model, or an integration.`,
      };
    }
    if (entry.configKeys.includes('controllerId') && scopes.length === 0 && !hasSeriesSource) {
      // A source-array present but with no fully-named entry is still "--" — say which half is missing.
      if (hasPartialItem) {
        return {
          error: `Tile ${i + 1} (${entry.id}) has items, but none names both a controllerId and a symbolId, so it would render "--". Give each item a controllerId and a symbolId.`,
        };
      }
      return {
        error:
          `Tile ${i + 1} (${entry.id}) has no source. That tile would render "--" with nothing to say why. ` +
          'Give it a controllerId for a PLC or CNC symbol, a modelId for a statechart variable, or an integrationId for an external path.',
      };
    }

    /*
     * ...AND THAT THE SOURCE IT NAMES ACTUALLY EXISTS.
     *
     * The check above refuses a MISSING binding for the reason in the comment above it — a tile
     * that binds to nothing renders "--" and reports nothing wrong. A binding to a controller
     * that does not exist produces the IDENTICAL dead screen: the subscription is planned, no
     * values ever match it, no fault is raised, and the board simply lies. Refusing one and
     * accepting the other checks the typo a model is least likely to make while waving through
     * the one it is most likely to make.
     *
     * Observed doing exactly that: every tile of a generated board stored controllerId "Core",
     * the display name, where the controller's id is "core". The board wrote, read back, and
     * verified clean; all four tiles sat at "collecting..." forever.
     */
    if (controllers && typeof config.controllerId === 'string' && config.controllerId.trim()) {
      const r = resolveControllerId(config.controllerId.trim(), controllers);
      if ('error' in r) return { error: `Tile ${i + 1} (${entry.id}): ${r.error}` };
      if (r.renamedFrom) {
        config.controllerId = r.id;
        normalized.push(`tile ${i + 1}: "${r.renamedFrom}" -> "${r.id}"`);
      }
    }
    /*
     * Every ITEM of a per-item source gets the same controller check the single source got above — an
     * item naming "Core" where the id is "core" is the same silent dead reading, just one row of a
     * legend or list instead of the whole tile. Resolve and normalise each in place so a
     * read-back-and-write-back both passes and is corrected once, exactly like the top-level pair.
     */
    if (controllers && hasSeriesSource) {
      for (const [j, s] of perItemSources.entries()) {
        const r = resolveControllerId((s.controllerId as string).trim(), controllers);
        if ('error' in r) return { error: `Tile ${i + 1} (${entry.id}), item ${j + 1}: ${r.error}` };
        if (r.renamedFrom) {
          s.controllerId = r.id;
          normalized.push(`tile ${i + 1} item ${j + 1}: "${r.renamedFrom}" -> "${r.id}"`);
        }
      }
    }
    // A per-item widget carries its sources in an array (handled above), so the top-level symbolId is
    // only required when the tile is NOT per-item-sourced.
    if (entry.configKeys.includes('symbolId') && !hasSeriesSource) {
      const v = config.symbolId;
      if (typeof v !== 'string' || !v.trim()) {
        // WHERE to find one differs by source: a controller's symbols come from
        // dashboards.symbols; a model's do not, because they are not a catalog to
        // search — models.read already returned the whole list as inputs/outputs.
        // Sending a model tile through dashboards.symbols is exactly the dead end
        // that burned five tool calls in a live conversation before the model
        // gave up on that controllerId and found symbols.search instead.
        const hint = config.modelId
          ? 'Its published variables were already in the inputs/outputs of the models.read call for that model — use one of those names.'
          : config.integrationId
            ? "Read that integration's schema for a path."
            : 'Find one with dashboards.symbols and pass the path it returns.';
        return {
          error: `Tile ${i + 1} (${entry.id}) has no symbolId. That tile would render "--" with nothing to say why. ${hint}`,
        };
      }
    }
    /*
     * Map markers are validated at AUTHORING time because the render side fails
     * SILENT (a bad pin just doesn't appear — a dashboard is no place for a
     * stack trace), which is exactly why the AUTHOR must hear about it here.
     * The fleet pins themselves take no config — the map always shows the real
     * roster; markers only ADD labelled places on top.
     */
    if (entry.id === 'map' && config.markers !== undefined) {
      if (!Array.isArray(config.markers)) {
        return { error: `Tile ${i + 1} (map): markers must be an array of { label, lat, lng }.` };
      }
      for (const [j, m] of (config.markers as unknown[]).entries()) {
        const x = m as Record<string, unknown>;
        const lat = typeof x?.lat === 'number' ? x.lat : NaN;
        const lng = typeof x?.lng === 'number' ? x.lng : typeof x?.lon === 'number' ? (x.lon as number) : NaN;
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          return { error: `Tile ${i + 1} (map): marker ${j + 1} needs a label plus numeric lat (-90..90) and lng (-180..180).` };
        }
      }
    }
    const w = Math.max(span(t.w, 4), entry.minW);
    const h = Math.max(span(t.h, 3), entry.minH);
    tiles.push({
      // A by-value tile keeps the id it had, so a later read-and-update finds it again.
      id: widget.startsWith('custom:') ? widget.slice('custom:'.length) : `${entry.id}-${i + 1}`,
      source: entry.source,
      // The catalog's defaults UNDER the author's config, so a tile the model
      // configured partially still renders rather than rendering blank.
      config: { ...entry.defaults, ...config },
      layout: { x: span(t.x, 0), y: span(t.y, 0), w, h },
      title: typeof t.title === 'string' && t.title.trim() ? t.title.trim() : undefined,
    });
  }
  return { tiles, normalized };
}

/*
 * DOES THE SYMBOL A TILE NAMES ACTUALLY EXIST, AND IS IT READABLE?
 *
 * The controller check upstream closes half of this. The other half is the symbol, and it fails
 * exactly the same way: a path that does not resolve is subscribed to, never matches, raises no
 * fault, and renders "--" forever. Two distinct ways to get it wrong, both silent:
 *
 *   - the path is simply not there (a typo, a stale path, a plausible invention)
 *   - the path IS there but names a CONTAINER — a branch of the symbol tree rather than a
 *     reading. dashboards.symbols already refuses to offer these for exactly this reason
 *     ("would produce tiles that render nothing"), but nothing stopped a tile binding one
 *     anyway. A real board hit this: "CPU" is an object whose numeric leaf is "CPU.Usage (%)".
 *
 * ONLY POSITIVE EVIDENCE OF ABSENCE REFUSES. If the lookup itself fails — transport blip, a
 * controller mid-reconnect — the write proceeds and says it could not verify. Refusing to save
 * someone's board because a catalogue read blipped is a worse failure than the one being
 * prevented, and it is the kind that makes people stop trusting the tool.
 */
const VERIFY_CONCURRENCY = 8;

interface SymbolLookup {
  searchSymbols(controllerId: string, query: string, limit: number): Promise<{ path: string; childCount: number }[]>;
}

/**
 * WAIT FOR THE ROSTER TO SAY IT, before telling the model it happened.
 *
 * A write returns when the gateway has accepted it; the board list this manager holds learns of
 * it a beat later, from the roster stream (deliberately — no optimistic updates, see the manager).
 * That beat is ~200 ms on a LAN, and a model chains calls faster than that: create, then read it
 * back, then open it — and the second call found "no dashboard" for something the first had just
 * confirmed. Same for update (a stale digest) and delete (a board still listed).
 *
 * So each write resolves only once the roster reflects it, and the model's next call sees what it
 * was told. Bounded: a roster that never catches up (a dropped stream) returns after the cap with
 * the write still reported as accepted, because it was — and the caller says the list may lag.
 */
const ROSTER_CATCHUP_MS = 3000;
async function rosterShows(dashboards: ResolvedGateway['entry']['dashboards'], done: () => boolean): Promise<boolean> {
  if (done()) return true;
  const target = dashboards as unknown as Partial<EventTarget>;
  if (typeof target.addEventListener !== 'function') return false; // a fixture with no stream: what it has now is all it has
  return new Promise<boolean>((resolve) => {
    const finish = (ok: boolean): void => {
      clearTimeout(timer);
      target.removeEventListener!('change', onChange);
      resolve(ok);
    };
    const onChange = (): void => {
      if (done()) finish(true);
    };
    const timer = setTimeout(() => finish(done()), ROSTER_CATCHUP_MS);
    target.addEventListener!('change', onChange);
  });
}
/** The one path a board is opened by, spelled out for the model. A link the model composed itself
 *  once dropped the gateway segment and led to the home page. */
const boardPath = (gatewayId: string, id: string): string => `/gateways/${gatewayId}/dashboards/${id}`;

const ROSTER_LAG_NOTE = ' The board list has not caught up yet — wait a moment before reading it back.';

/** Every (controller, symbol) a board's tiles bind — the top-level pair AND every per-item array
 *  (a chart's `symbols`, an alarms/headroom `items`, …). One `add`, one JSON key, so the same pair
 *  reached two ways dedupes. Used both to verify a board's bindings and to subtract the ones already
 *  on it. */
function bindingPairs(tiles: readonly DashboardTile[]): Map<string, { controllerId: string; symbolId: string }> {
  const pairs = new Map<string, { controllerId: string; symbolId: string }>();
  const add = (controllerId: unknown, symbolId: unknown): void => {
    if (typeof controllerId !== 'string' || !controllerId.trim()) return;
    if (typeof symbolId !== 'string' || !symbolId.trim()) return;
    pairs.set(JSON.stringify([controllerId, symbolId]), { controllerId, symbolId });
  };
  for (const t of tiles) {
    const c = t.config as { controllerId?: unknown; symbolId?: unknown };
    add(c.controllerId, c.symbolId);
    // A per-item widget names a controller+symbol per entry — any config array of { controllerId,
    // symbolId }, whatever the key (chart `symbols`, alarms/headroom `items`, …).
    for (const v of Object.values(t.config as Record<string, unknown>)) {
      if (!Array.isArray(v)) continue;
      for (const s of v) {
        if (s && typeof s === 'object') add((s as Record<string, unknown>).controllerId, (s as Record<string, unknown>).symbolId);
      }
    }
  }
  return pairs;
}

async function verifyBindings(
  lookup: SymbolLookup,
  tiles: readonly DashboardTile[],
  priorTiles?: readonly DashboardTile[],
): Promise<{ error?: string; unverified?: boolean }> {
  // Only bindings NEW to the board are checked. A pair already present on the prior board was already
  // live — and verified when it was added — so a write-back that keeps a tile does not re-check it.
  // Without this, a one-tile edit re-verifies every binding on the whole board (a symbol lookup per
  // unique pair), which on a large board is seconds of latency for nothing. Keyed identically to the
  // new set, so the subtraction is exact. On create there is no prior board, so everything is checked.
  const pairs = bindingPairs(tiles);
  if (priorTiles) for (const k of bindingPairs(priorTiles).keys()) pairs.delete(k);
  if (pairs.size === 0) return {};

  const todo = [...pairs.values()];
  let unverified = false;
  const problems: string[] = [];

  for (let i = 0; i < todo.length; i += VERIFY_CONCURRENCY) {
    await Promise.all(
      todo.slice(i, i + VERIFY_CONCURRENCY).map(async ({ controllerId, symbolId }) => {
        let rows: { path: string; childCount: number }[];
        try {
          // The path itself is the query; the exact match is picked out of what comes back.
          rows = await lookup.searchSymbols(controllerId, symbolId, 50);
        } catch {
          unverified = true; // fail OPEN — see the note above
          return;
        }
        const hit = rows.find((r) => r.path === symbolId);
        if (!hit) {
          const near = rows.filter((r) => r.childCount === 0).slice(0, 3).map((r) => r.path);
          problems.push(
            `"${symbolId}" is not a symbol on "${controllerId}"` +
              (near.length ? ` — did you mean ${near.map((n) => `"${n}"`).join(', ')}?` : '.'),
          );
        } else if (hit.childCount > 0) {
          const leaves = rows.filter((r) => r.childCount === 0 && r.path.startsWith(`${symbolId}.`)).slice(0, 3).map((r) => r.path);
          problems.push(
            `"${symbolId}" on "${controllerId}" is a container, not a reading, so a tile bound to it shows nothing` +
              (leaves.length ? ` — bind one of its values instead: ${leaves.map((n) => `"${n}"`).join(', ')}.` : '.'),
          );
        }
      }),
    );
  }

  if (problems.length) {
    return {
      error:
        `${problems.length === 1 ? 'A tile binds to something that will not render' : `${problems.length} tiles bind to something that will not render`}: ` +
        problems.join(' ') +
        ' Nothing was saved — find the real path with dashboards.symbols and send the board again.',
    };
  }
  return { unverified };
}

/**
 * Every widget a tile can name: the built-ins, the marketplace's shelf, and the org's own.
 *
 * One list, read by the toolbox (`dashboards.widgets`) and by the @ mentions, so the two can never
 * offer different catalogues. The shelf is read live — a widget published there is offered the
 * moment it is on the shelf. A card that is merely a built-in under its own name (dial, chart,
 * note) is not listed twice; a preset of one (tank, battery) is, because it places differently.
 */
export function allWidgets(installed?: ReadonlyMap<string, CatalogEntry>): CatalogEntry[] {
  const shelf = officialWidgets()
    .filter((w) => !w.builtIn || w.id !== w.entry.id)
    .map((w) => officialWidgetEntry(w.id))
    .filter((e): e is CatalogEntry => !!e);
  return [...CATALOG, ...shelf, ...(installed?.values() ?? [])];
}

export function registerDashboardTools(deps: DashboardToolDeps): () => void {
  /*
   * The catalog. A CLIENT tool because the catalog is this build's, not the
   * server's — a widget added to the app is offered the moment the page loads,
   * with no worker deploy and no schema to publish.
   *
   * Authoring is impossible without it: a model that cannot see which widgets
   * exist or what each one is configured with can only guess, and every guess
   * costs a refused write.
   */
  const widgets: ClientTool = {
    name: 'dashboards.widgets',
    // the widget CATALOGUE — what tile types exist, no plant data in it at all.
    permission: Permission.UNSPECIFIED,
    description:
      'List the dashboard widgets available to build with, each with the config keys it expects and its minimum size: the ' +
      "built-in tiles, everything on the marketplace (named marketplace:<id> — place those exactly like a built-in, no install step), and the org's own widgets (org:<id>). " +
      'Call this BEFORE dashboards.create or dashboards.update — tile widgets must be named from this list, and a tile is ' +
      'configured with these keys. Most widgets take a controllerId and a symbolId, which you get from the symbol catalog.',
    parameters: { type: 'object', properties: {}, additionalProperties: false },
    run: async () => {
      // Installed widgets are offered ALONGSIDE built-ins and in the same
      // shape: a model should not have to know which catalog a widget came
      // from to place it, any more than a person does. Fresh, because the
      // widget most worth listing is the one authored a step ago.
      await deps.refreshInstalledWidgets?.();
      // Built-ins, the marketplace shelf read live, and the org's own — see `allWidgets`.
      const all = allWidgets(deps.installedWidgets?.());
      return {
        ok: true,
        message: `${all.length} widgets: ${all.map((c) => c.id).join(', ')}.`,
        data: {
          widgets: all.map((c) => ({
            id: c.id,
            title: c.title,
            summary: c.summary,
            configKeys: c.configKeys,
            defaults: c.defaults,
            minW: c.minW,
            minH: c.minH,
          })),
        },
      };
    },
  };

  /*
   * WHAT THERE IS TO BIND TO.
   *
   * Authoring without this is guessing: a tile names a symbol, and a model that
   * cannot look one up invents plausible-looking paths that bind to nothing.
   * Returns the READABLE path — the tool encodes it for the wire, so the model
   * never has to know the encoding exists.
   */
  const symbols: ClientTool = {
    name: 'dashboards.symbols',
    // reads the controller's symbol catalogue, same as Dashboards/Get.
    permission: Permission.SYMBOL_VIEW,
    description:
      "Search a CONTROLLER's (PLC or CNC) symbols, to find what a dashboard tile should read. " +
      "Returns matching symbol paths with their data types. Use the paths it returns verbatim as a tile's symbolId. " +
      'Search before you build: a tile bound to a symbol that does not exist renders nothing. ' +
      'NOT for a model-scoped tile (config.modelId): a model has no separate symbol catalog — its published ' +
      'variables are exactly the inputs/outputs already returned by models.read for that model.',
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        controllerId: { type: 'string', description: 'Which controller, e.g. from the plant snapshot.' },
        query: { type: 'string', description: 'Text to match against symbol paths, e.g. "temperature" or "Motor1".' },
        limit: { type: 'number', description: 'How many to return, default 40, max 200.' },
      },
      required: ['controllerId'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const wanted = String(args.controllerId ?? '').trim();
      if (!wanted) {
        const ids = entry.controllers.all.map((c) => c.id);
        return { ok: false, message: `A controllerId is required. The controllers on this relay are: ${ids.join(', ') || 'none'}.` };
      }
      // Accept id OR display name, case-insensitively — the same tolerance symbols.search gives:
      // a model that read a name off the plant snapshot ("Core") should not have to also guess the
      // exact-case id ("core") a first attempt at that name will otherwise silently fail against.
      const match = entry.controllers.all.find(
        (c) => c.id.toLowerCase() === wanted.toLowerCase() || c.name.toLowerCase() === wanted.toLowerCase(),
      );
      if (!match) {
        const known = entry.controllers.all.map((c) => `${c.name} (${c.id})`);
        return {
          ok: false,
          message: `No controller "${wanted}" on this relay.${known.length ? ` The controllers here are: ${known.join(', ')}.` : ' This relay has no controllers.'}`,
        };
      }
      const controllerId = match.id;
      const limit = Math.min(Math.max(Math.trunc(Number(args.limit ?? 40)) || 40, 1), 200);
      try {
        const rows = await entry.dashboards.searchSymbols(controllerId, String(args.query ?? ''), limit);
        // Containers are branches of the tree, not readings — offering them as
        // bindable would produce tiles that render nothing.
        const leaves = rows.filter((r) => r.childCount === 0);
        if (leaves.length === 0) {
          return {
            ok: true,
            message: `No readable symbols on "${controllerId}" match that. Try a shorter query, or an empty one to see what is there.`,
            data: { symbols: [] },
          };
        }
        return {
          ok: true,
          message: `${leaves.length} symbol${leaves.length === 1 ? '' : 's'} on "${controllerId}"${args.query ? ` matching "${String(args.query)}"` : ''}.`,
          data: { symbols: leaves.map((r) => ({ symbolId: r.path, dataType: r.dataType, permission: r.permission })) },
        };
      } catch (e) {
        return {
          ok: false,
          message: `The symbol catalog could not be read: ${e instanceof Error ? e.message : String(e)}`,
          retryable: true,
        };
      }
    },
  };

  /*
   * CREATE — the metered call.
   *
   * Separate from update on purpose, and the separation is load-bearing rather
   * than cosmetic: an org at its dashboard ceiling must still be able to EDIT
   * the boards it has. If one procedure did both, "you are at your limit" would
   * also mean "you can no longer fix your existing screens", which is a bad day
   * caused by an implementation detail.
   */
  /*
   * READ A BOARD AS THE STRUCTURE IT IS — the tool whose absence made editing dangerous.
   *
   * dashboards.update REPLACES the tile list when one is sent, so changing a single tile means
   * sending every other tile back unchanged. The only read the model had was the server's
   * ReadDashboard, which returns PROSE — deliberately, it renders a board for a person. So editing
   * meant reconstructing a layout from a description: guessing widget ids, config keys and grid
   * positions, then overwriting the real board with the guess. Every tile it reconstructed slightly
   * wrong was silently destroyed, and the failure looked like the model being careless rather than
   * like a missing verb.
   *
   * This returns the board in exactly the shape create and update ACCEPT, so the path is read →
   * change the one tile → write. No reconstruction, nothing to guess, and a round trip that cannot
   * lose what it did not touch.
   *
   * The client can do this trivially — `dashboards.body(id)` is the same call update already makes
   * internally before merging. The data was one function away the whole time; only the model could
   * not reach it.
   */
  /*
   * WHICH BOARDS EXIST, ACROSS THE WHOLE FLEET, IN ONE CALL.
   *
   * This did not exist, and its absence was not a missing convenience — it was the reason an
   * assistant could not answer "what dashboards do I have". Asked to summarize them it had
   * `dashboards.read`, which needs an id it had no way to obtain, so the only honest answer was
   * to decline. The read tool's own parameter text already pointed at `dashboards.list` as the
   * place ids come from; the tool it named was never built.
   *
   * OMITTING `gateway` LISTS EVERY RELAY, which deliberately departs from GATEWAY_PARAM's
   * "omit to use whichever gateway is open". For a per-board verb that default is right — you
   * act on the thing in front of you. For an ENUMERATION it is wrong: a model asking what
   * exists cannot know how many relays there are, so a per-relay default turns one question
   * into N calls it must first discover the bound of, and any relay it forgets is silently
   * missing from the answer. The roster is already resident for every claimed box (`entries()`
   * is the shell's reconciled fleet), so the whole-fleet answer costs no network at all — one
   * call, no round trips, nothing to paginate.
   *
   * Summaries only, never bodies: id, title, description, tile count and last-changed are what
   * a summary or an id-lookup needs, and they are already in memory. Fetching every board's
   * tiles to answer "what boards are there" would be the expensive path to a worse answer;
   * `dashboards.read` is one call away once an id is in hand.
   */
  const list: ClientTool = {
    name: 'dashboards.list',
    // The board ROSTER — titles and counts the operator authored. Same read scope as reading one.
    permission: Permission.SYMBOL_VIEW,
    description:
      'List the dashboards that exist, with their id, title, description, tile count and when each ' +
      'last changed. Omit `gateway` to list boards on EVERY relay in the organization — that is the ' +
      'one call that answers "what dashboards do I have" or "where does this board live". Pass ' +
      '`gateway` to narrow to one relay. Returns summaries, not contents: use dashboards.read with ' +
      'an id from here to see a board\'s tiles.',
    parameters: {
      type: 'object',
      properties: {
        gateway: {
          type: 'string',
          description:
            'Optional. Which relay, by id or name, to list. OMIT THIS to list every relay in the ' +
            'organization, which is usually what you want.',
        },
      },
      additionalProperties: false,
    },
    run: (args) => {
      const wanted = typeof args.gateway === 'string' ? args.gateway.trim() : '';

      // Named relay: resolve it so an unknown name gets the roster back rather than an empty list.
      // "no such relay" and "that relay has no boards" are different answers and a model that
      // cannot tell them apart will report the wrong one.
      let scope: readonly ResolvedGateway[];
      if (wanted) {
        const gw = resolveGateway(deps, wanted);
        if (!gw.ok) return { ok: false, message: gw.message };
        scope = [gw.value];
      } else {
        scope = deps.entries().map((e) => ({
          entry: e,
          id: e.gateway.deviceFingerprint,
          name: e.gateway.displayName || e.gateway.hostname || e.gateway.deviceFingerprint,
        }));
      }

      const boards = scope.flatMap((g) =>
        g.entry.dashboards.all.map((d) => ({
          id: d.id,
          title: d.title,
          description: d.description ?? '',
          tileCount: d.tileCount,
          updatedAtMs: d.updatedAtMs,
          gateway: g.id,
          gatewayName: g.name,
        })),
      );

      // The relays that WERE looked at are part of the answer. Without them "0 dashboards" is
      // ambiguous between "you have none" and "I only checked one box", which is exactly the
      // uncertainty that makes a model hedge or guess.
      const looked = scope.map((g) => g.name);
      if (!boards.length) {
        return {
          ok: true,
          message: `No dashboards on ${looked.length === 1 ? looked[0] : `any of the ${looked.length} relays (${looked.join(', ')})`}.`,
          data: { dashboards: [], relaysSearched: looked },
        };
      }
      const where = boards.reduce<Record<string, number>>((acc, b) => {
        acc[b.gatewayName] = (acc[b.gatewayName] ?? 0) + 1;
        return acc;
      }, {});
      return {
        ok: true,
        message:
          `${boards.length} dashboard${boards.length === 1 ? '' : 's'} across ${looked.length} relay${looked.length === 1 ? '' : 's'}: ` +
          Object.entries(where)
            .map(([n, c]) => `${n} (${c})`)
            .join(', ') +
          '.',
        data: { dashboards: boards, relaysSearched: looked },
      };
    },
  };

  const read: ClientTool = {
    name: 'dashboards.read',
    permission: Permission.SYMBOL_VIEW,
    description:
      'Read one dashboard as STRUCTURE: its title, description, and every tile with the widget, ' +
      'config and grid position it was built from — the same shape dashboards.create and ' +
      'dashboards.update take. Call this before editing a board so you can change one tile and send ' +
      'the rest back exactly as they were; dashboards.update replaces the whole tile list, so ' +
      'editing without reading first will destroy the tiles you did not mean to touch.',
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        id: { type: 'string', description: 'The dashboard id, from dashboards.list or the roster.' },
      },
      required: ['id'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const id = String(args.id ?? '').trim();
      const summary = entry.dashboards.get(id);
      if (!summary) {
        const known = entry.dashboards.all.map((d) => `${d.id} ("${d.title}")`);
        return {
          ok: false,
          message: `No dashboard "${id}" on this relay.${known.length ? ` The dashboards here are: ${known.join(', ')}.` : ''}`,
        };
      }
      const board = await entry.dashboards.body(id);
      if (!board) {
        return {
          ok: false,
          message: `"${id}" is listed but its contents could not be read, so there is nothing to edit from yet. This is usually temporary.`,
          retryable: true,
        };
      }
      /*
       * The widget's catalogue id is recovered from the tile's SOURCE.
       *
       * A stored tile carries its widget's source, not the catalogue name it came from — a board
       * carries its widgets so it cannot break when a catalogue entry changes underneath it. This
       * used to read the name back by stripping `-<n>` off the tile ID, on the stated understanding
       * that the writer always spells an id that way, and the comment here said this was the one
       * place that would have to change if that stopped holding.
       *
       * It has. `DashboardManager.addTile` — the path a PERSON takes to add a marketplace tile —
       * writes `<slug>-<n>-<ts>`, so stripping one suffix left `native-map-1`, a widget name
       * nothing recognises, and `dashboards.update` then refused to write the board back. A board a
       * human had touched could not be round-tripped by the assistant at all.
       *
       * The source IS the widget's identity: it is what the runtime executes, and `catalogForSource`
       * already maps it. Reading from there needs no agreement between two writers about how to
       * spell an id. The strip stays as the fallback for a source the catalogue does not know, which
       * is the honest answer for an installed widget this build has never seen.
       */
      const tiles = board.tiles.map((t) => ({
        // A tile whose source no catalogue knows — one a widget author wrote for this board — reads
        // back as `custom:<tile id>`; sent back under that name, it keeps its source.
        widget: catalogForSource(String(t.source ?? ''))?.id ?? officialForSource(String(t.source ?? ''))?.id ?? (String(t.source ?? '').startsWith('native:') || (deps.installedWidgets?.() ?? new Map()).has(String(t.id ?? '').replace(/-\d+$/, '')) ? String(t.id ?? '').replace(/-\d+$/, '') : `custom:${String(t.id ?? '')}`),
        config: t.config ?? {},
        x: t.layout?.x ?? 0,
        y: t.layout?.y ?? 0,
        w: t.layout?.w ?? 0,
        h: t.layout?.h ?? 0,
        ...(t.title ? { title: t.title } : {}),
      }));
      return {
        ok: true,
        message:
          `"${summary.title}" has ${tiles.length} tile${tiles.length === 1 ? '' : 's'}. ` +
          `The tiles below are in the shape dashboards.update takes — send them back unchanged except for what you mean to change.`,
        data: { id, title: summary.title, description: board.description ?? '', tiles },
      };
    },
  };

  const create: ClientTool = {
    name: 'dashboards.create',
    // Dashboards/Create.
    permission: Permission.DASHBOARD_CREATE,
    description:
      'Create a new dashboard. Call dashboards.widgets first to see what you can ' +
      'place, and read the symbol catalog for the controllerId/symbolId a tile should bind to. ' +
      "Creating counts against the organization's dashboard allowance and may be refused when it is full.",
    mutates: true,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        id: { type: 'string', description: 'A short url-safe id, letters/numbers/dash/underscore, e.g. "line-3-overview".' },
        title: { type: 'string', description: 'What the dashboard is called, shown to people.' },
        description: {
          type: 'string',
          description:
            'One or two sentences: what this board is for and who reads it. Write one — it is what somebody scanning a list of ' +
            'dashboards reads to decide whether this is the screen they want, and a board without one is a title and a guess.',
        },
        tiles: {
          type: 'array',
          description: 'The tiles, in a 12-column grid. Each: {widget, config, x, y, w, h, title?}.',
          items: {
            type: 'object',
            properties: {
              widget: {
                type: 'string',
                description:
                  'A widget id from dashboards.widgets — a built-in, marketplace:<id>, or org:<id>. For a widget a Widget Author built for you, "custom" together with `fromAgent` (its source is copied from what that agent returned — never retype it); a by-value tile read back from a board is named custom:<tile id> and keeps its source when sent back under that name.',
              },
              fromAgent: {
                type: 'string',
                description:
                  'The exact label you gave the Widget Author whose widget this tile places. It must have finished. Its source, config keys and title are taken from its deliverable; you send none of them.',
              },
              source: { type: 'string', description: 'The widget module source, for a widget placed by value without an agent. Prefer fromAgent; the board carries it either way and nothing is written to any store.' },
              configKeys: { type: 'array', items: { type: 'string' }, description: 'With `source`: the config keys that widget declares, so its bindings are checked.' },
              config: {
                type: 'object',
                description:
                  'Settings for that widget — see its configKeys. The SOURCE is exactly one of controllerId (a PLC or ' +
                  "CNC symbol), modelId (a statechart's published variable) or integrationId (an external path), plus " +
                  'symbolId for the thing being read.',
              },
              x: { type: 'number', description: 'Column, 0-based, grid is 12 wide.' },
              y: { type: 'number', description: 'Row, 0-based.' },
              w: { type: 'number', description: 'Width in columns.' },
              h: { type: 'number', description: 'Height in rows.' },
              title: { type: 'string', description: "The tile's own heading. Optional." },
            },
            required: ['widget'],
          },
        },
      },
      required: ['id', 'title', 'tiles'],
      additionalProperties: false,
    },
    run: async (args, ctx) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const id = String(args.id ?? '').trim();
      const title = String(args.title ?? '').trim();
      if (!ID_OK.test(id))
        return {
          ok: false,
          message: `"${id}" is not a usable dashboard id. Use letters, numbers, dashes or underscores, up to 64 characters, starting with a letter or number.`,
        };
      if (!title) return { ok: false, message: 'A title is required — it is what a person sees in the dashboard list.' };
      if (entry.dashboards.get(id))
        return {
          ok: false,
          message: `A dashboard "${id}" already exists on this relay. Use dashboards.update to change it, or pick another id.`,
        };

      const roster = entry.controllers.all.map((c) => ({ id: c.id, name: c.name }));
      const inlined = await inlineAgentSources(args.tiles, ctx?.runId ?? '', deps.agentDeliverable);
      if (inlined.error) return { ok: false, message: inlined.error };
      if (!tilesResolvable(inlined.tiles, deps.installedWidgets?.())) await deps.refreshInstalledWidgets?.();
      const { tiles, error, normalized } = buildTiles(inlined.tiles, deps.installedWidgets?.(), roster);
      if (error || !tiles) return { ok: false, message: error ?? 'The tiles could not be read.' };

      const verified = await verifyBindings(entry.dashboards, tiles);
      if (verified.error) return { ok: false, message: verified.error };

      const board: Dashboard = {
        id,
        title,
        description: String(args.description ?? '').trim(),
        digest: '',
        updatedAtMs: Date.now(),
        tileCount: tiles.length,
        tiles,
      };
      // Attributed as the machine actor it is: the trail reads
      // "AI assistant on behalf of <person>", never just the person.
      const r = await entry.dashboards.create(board, 'AI assistant');
      if (!r.ok) {
        /*
         * THE THREE REFUSALS ARE NOT INTERCHANGEABLE TO A MODEL EITHER.
         *
         * Told "you are over your limit" for what was actually a momentary
         * failure, a model stops and reports a billing problem that does not
         * exist. Told nothing, it retries an unretryable refusal until its cap.
         * So the outcome carries the server's own classification rather than a
         * guess made here.
         */
        return {
          ok: false,
          message: r.entitlement
            ? `This organization is at its dashboard limit: ${r.error} Tell the user — creating another needs a plan change or an existing board removed.`
            : r.retryable
              ? `The relay could not complete that just now: ${r.error} Nothing was created and nothing changed; this is worth one retry.`
              : `The relay refused that: ${r.error}`,
          retryable: !!r.retryable,
        };
      }
      const listed = await rosterShows(entry.dashboards, () => !!entry.dashboards.get(id));
      return {
        ok: true,
        // Say when a binding was corrected. A silent fix teaches the model nothing and hides a
        // real ambiguity from the person who asked; naming it costs one clause.
        message:
          `Created "${title}" with ${tiles.length} tile${tiles.length === 1 ? '' : 's'} on "${gw.value.name}". It is at ${boardPath(gw.value.id, id)} — link it exactly so the person can open it in one click.` +
          (normalized?.length ? ` Bound to controller ids rather than names (${normalized.join('; ')}) — use the id next time.` : '') +
          (verified.unverified ? ' The symbol catalogue could not be reached, so the bindings were not checked — open the board to confirm the tiles render.' : '') +
          (listed ? '' : ROSTER_LAG_NOTE),
        data: { dashboardId: id, tileCount: tiles.length },
      };
    },
  };

  /*
   * UPDATE — replace-only, and never metered.
   *
   * It REPLACES the tile list rather than merging, which is the honest shape
   * for a document: a partial update would need a merge rule, and the one thing
   * worse than resending a board is two clients disagreeing about how their
   * edits combined. So the model must read the board first, which the required
   * digest below also forces.
   */
  const update: ClientTool = {
    name: 'dashboards.update',
    // Dashboards/Save, which is the same gate as Create.
    permission: Permission.DASHBOARD_CREATE,
    description:
      'Edit an existing dashboard: its title, its description, its tiles, or any combination. ' +
      'Anything you leave out is left alone — so changing a title or writing a description does NOT require resending the tiles. ' +
      'If you DO send tiles they REPLACE the existing set rather than adding to it, so read the dashboard first and include ' +
      'the ones you are keeping. Editing never counts against the dashboard allowance.',
    mutates: true,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        id: { type: 'string', description: 'The dashboard to edit.' },
        title: { type: 'string', description: 'The title after the edit. Omit to leave it unchanged.' },
        description: {
          type: 'string',
          description:
            'One or two sentences on what the board is for. Omit to leave it unchanged. This is the field to write when ' +
            'somebody asks you to describe or document a dashboard.',
        },
        tiles: {
          type: 'array',
          description: 'The COMPLETE tile list, same shape as dashboards.create. Omit to leave the tiles exactly as they are.',
          items: { type: 'object' },
        },
      },
      required: ['id'],
      additionalProperties: false,
    },
    run: async (args, ctx) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const id = String(args.id ?? '').trim();
      const summary = entry.dashboards.get(id);
      if (!summary) {
        const known = entry.dashboards.all.map((d) => `${d.id} ("${d.title}")`);
        return {
          ok: false,
          message: `No dashboard "${id}" on this relay.${known.length ? ` The dashboards here are: ${known.join(', ')}.` : ''}`,
        };
      }
      const title = String(args.title ?? '').trim() || summary.title;

      /*
       * OMITTED MEANS UNCHANGED, for every field.
       *
       * The tile list REPLACES when it is sent — a partial tile update would
       * need a merge rule, and the one thing worse than resending a board is
       * two clients disagreeing about how their edits combined. But requiring
       * the tiles in order to fix a title made every metadata edit a
       * hundred-tile round trip, and a model that resends a list it did not
       * mean to change is a model one bad read away from destroying a screen.
       *
       * So: send tiles to replace them, leave them out to keep them. That makes
       * this ONE edit verb rather than a family of them, which is also what
       * stops a later `dashboards.describe` existing with its own subtly
       * different conflict behaviour.
       */
      const current = await entry.dashboards.body(id);
      if (!current) {
        return {
          ok: false,
          message: `"${id}" is in the list but its contents could not be read, so it was not edited. This is usually temporary.`,
          retryable: true,
        };
      }
      let tiles = current.tiles;
      if (args.tiles !== undefined) {
        const inlined = await inlineAgentSources(args.tiles, ctx?.runId ?? '', deps.agentDeliverable);
        if (inlined.error) return { ok: false, message: inlined.error };
        if (!tilesResolvable(inlined.tiles, deps.installedWidgets?.())) await deps.refreshInstalledWidgets?.();
        const built = buildTiles(inlined.tiles, deps.installedWidgets?.(), entry.controllers.all.map((c) => ({ id: c.id, name: c.name })), current.tiles);
        if (built.error || !built.tiles) return { ok: false, message: built.error ?? 'The tiles could not be read.' };
        // Same gate as create. An update REPLACES the tile list, so an unrenderable binding sent
        // here does not just fail to appear — it takes a working tile's place. Passing the board's
        // CURRENT tiles limits the check to bindings NEW to the board: a write-back that keeps a
        // tile does not re-verify it, so adding one tile to a large board is not a full re-scan.
        const checked = await verifyBindings(entry.dashboards, built.tiles, current.tiles);
        if (checked.error) return { ok: false, message: checked.error };
        tiles = built.tiles;
      }
      const description = args.description === undefined ? current.description : String(args.description).trim();

      /*
       * THE BACKGROUND IS CARRIED THROUGH, and leaving it out is the bug this line exists to
       * prevent.
       *
       * A save replaces the whole document. This verb builds a fresh board from the fields it was
       * given, so any stored field it does not mention is silently dropped — and a background
       * would be dropped by an edit that only changed a title. The failure is invisible at the
       * moment it happens and is discovered by somebody opening the board later and finding their
       * picture gone, with the edit that did it long since scrolled away.
       */
      const board: Dashboard = {
        id,
        title,
        description,
        digest: summary.digest,
        updatedAtMs: Date.now(),
        tileCount: tiles.length,
        tiles,
        ...(current.background ? { background: current.background } : {}),
      };
      /*
       * The digest we last saw rides the write. A save whose base has moved is
       * REFUSED by the gateway rather than merged — if a colleague edited this
       * board while the assistant was composing, silently overwriting their
       * work is the failure mode worth paying a round trip to avoid.
       */
      const r = await entry.dashboards.save(board, summary.digest, 'AI assistant');
      if (!r.ok) return { ok: false, message: `The relay refused that write: ${r.error}` };
      // The next call reads the NEW digest; returning before the roster carries it would hand the
      // model a stale base and its next save a conflict it did not cause.
      const listed = await rosterShows(entry.dashboards, () => entry.dashboards.get(id)?.digest !== summary.digest);
      const changed = [
        title !== summary.title ? 'title' : '',
        args.description !== undefined ? 'description' : '',
        args.tiles !== undefined ? `tiles (now ${tiles.length})` : '',
      ].filter(Boolean);
      return {
        ok: true,
        // Naming what changed rather than restating the board: a model told
        // only "updated" cannot tell an edit that landed from one that was a
        // no-op because every field was omitted.
        message:
          (changed.length ? `Updated "${title}": ${changed.join(', ')}. It is at ${boardPath(gw.value.id, id)} — link it exactly.` : `Nothing changed on "${title}" — every field was left out.`) +
          (listed ? '' : ROSTER_LAG_NOTE),
        data: { dashboardId: id, tileCount: tiles.length, changed },
      };
    },
  };

  /*
   * DELETE — asks for the title as well as the id, for the same reason
   * models.delete asks for the name. Every other tool here fails safely on a
   * wrong id; this one destroys a screen somebody built, and the model is
   * working from a list it fetched some turns ago. A mismatch catches an id
   * that has drifted before the consequence does.
   */
  const del: ClientTool = {
    name: 'dashboards.delete',
    // Dashboards/Delete.
    permission: Permission.DASHBOARD_DELETE,
    description:
      'Permanently delete a dashboard from the open relay. This cannot be undone. You must pass both the id and the exact ' +
      'current title — if they do not match, nothing is deleted. Ask the user before calling this.',
    mutates: true,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        id: { type: 'string', description: 'The dashboard to delete.' },
        title: { type: 'string', description: 'Its exact current title, as confirmation.' },
      },
      required: ['id', 'title'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const id = String(args.id ?? '').trim();
      const claimed = String(args.title ?? '').trim();
      const summary = entry.dashboards.get(id);
      if (!summary) return { ok: false, message: `No dashboard "${id}" on this relay, so nothing was deleted.` };
      if (claimed !== summary.title)
        return {
          ok: false,
          message: `Not deleted: "${id}" is titled "${summary.title}", not "${claimed}". Check you have the right dashboard, then call again with its real title.`,
        };
      const r = await entry.dashboards.delete(id);
      if (!r.ok) return { ok: false, message: `The relay refused that delete: ${r.error}` };
      const gone = await rosterShows(entry.dashboards, () => !entry.dashboards.get(id));
      return { ok: true, message: `Deleted "${summary.title}".` + (gone ? '' : ROSTER_LAG_NOTE), data: { dashboardId: id } };
    },
  };

  /*
   * THE PICTURE BEHIND THE TILES.
   *
   * Its own verb rather than a field on `dashboards.update`, and the reason is the bytes. Every
   * other field of a board is something a model can type; this one is a megabyte of image that has
   * to be fetched, decoded, resized and re-encoded before it can be stored — and putting that
   * behind an optional argument on the general edit verb would mean every update call carried a
   * parameter almost none of them use, described at length, in the prompt, forever.
   *
   * IT TAKES A REFERENCE, NOT AN IMAGE. The model names a picture that already exists in this
   * conversation — one `ai_admin_generate_image` made, or one a person attached — and this fetches
   * it through the AI plane. A model cannot hold bytes and must never be asked to: base64 in a tool
   * argument would be a hundred kilobytes of prompt for something it cannot look at.
   */
  const setBackground: ClientTool = {
    name: 'dashboards.setBackground',
    // A background IS the board: it is stored in the body and saved through Dashboards/Save, so it
    // takes the same permission every other edit to a board takes. Generating the picture is a
    // separate decision with its own key (ai:image:generate) on the tool that makes one.
    permission: Permission.DASHBOARD_CREATE,
    description:
      'Put a picture behind a dashboard, or take one away. Name an image that already exists in this conversation by its ' +
      '`imageSha` — generate one with the image tool first, or use one the person attached. The picture is stored WITH the ' +
      'board, so it survives this conversation and travels with the board. Prefer ABSTRACT images — textures, gradients, ' +
      'atmospheres — pale and low-contrast, close to the page colour, since the app is light by default and a dark or ' +
      'saturated picture is the wrong picture rather than one that needs more dim. `dim` blends the picture toward the page ' +
      'colour by that percentage (0 = as generated, 100 = gone): a quiet picture needs 10-30; a dark one at 70 is flat grey. ' +
      'Tiles sit on top of this and the numbers have to stay readable, which is the whole difference between a background ' +
      'that helps and one that ruins a screen.',
    mutates: true,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        id: { type: 'string', description: 'The dashboard to change.' },
        imageSha: {
          type: 'string',
          description:
            'The content address of an image in this conversation, as the image tool returned it. Omit together with `clear` ' +
            'to change only the fit or the dim of the background already there.',
        },
        fit: {
          type: 'string',
          enum: ['cover', 'contain', 'tile'],
          description: '`cover` fills the board and crops (the default); `contain` fits the whole picture; `tile` repeats it.',
        },
        dim: {
          type: 'number',
          description:
            'How much to fade the picture toward the page background, 0-90 percent. Higher is safer: a photograph behind small ' +
            'numbers destroys the contrast that makes them readable.',
        },
        clear: { type: 'boolean', description: 'Remove the background entirely.' },
      },
      required: ['id'],
      additionalProperties: false,
    },
    run: async (args, ctx) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const id = String(args.id ?? '').trim();
      const summary = entry.dashboards.get(id);
      if (!summary) {
        const known = entry.dashboards.all.map((d) => `${d.id} ("${d.title}")`);
        return { ok: false, message: `No dashboard "${id}" on this relay.${known.length ? ` The dashboards here are: ${known.join(', ')}.` : ''}` };
      }
      const current = await entry.dashboards.body(id);
      if (!current) {
        return { ok: false, message: `"${id}" is in the list but its contents could not be read, so it was not changed.`, retryable: true };
      }

      const clear = args.clear === true;
      const sha = String(args.imageSha ?? '').trim();
      if (!clear && !sha && !current.background) {
        return {
          ok: false,
          message: `"${summary.title}" has no background, so there is nothing to adjust. Name an \`imageSha\` to set one.`,
        };
      }

      let background = clear ? undefined : current.background;
      if (!clear && sha) {
        if (!deps.readConvFile) {
          return { ok: false, message: 'This app cannot read conversation files, so a background cannot be set from one here.' };
        }
        const conversationId = ctx?.conversationId ?? '';
        if (!conversationId) {
          return { ok: false, message: 'That image can only be read from inside a conversation, and this call arrived without one.' };
        }
        const raw = await deps.readConvFile(conversationId, sha).catch(() => null);
        if (!raw) {
          return {
            ok: false,
            message: `No image with that address is stored in this conversation. Generate one first, and use the address the tool returned.`,
          };
        }
        /*
         * RE-ENCODED BEFORE IT IS STORED, and the size is the reason the tool exists in this shape.
         *
         * The picture goes INTO the board, and everybody who opens that board downloads all of it.
         * A model hands back a one-to-two megabyte PNG; WebP at 1920 wide is a fifth of that and
         * indistinguishable behind a row of gauges. The gateway refuses anything over its cap, so
         * failing to compress here is a save that is refused for a limit nobody chose.
         */
        const { encodeBackground, sniffImageType } = await import('../../dashboards/background-encode');
        // READ FROM THE BYTES, not assumed. The blob store is content-addressed — the sha names the
        // bytes, not their label — so this is the only place the type can honestly come from.
        const kind = sniffImageType(raw);
        if (!kind) {
          return { ok: false, message: 'That address holds something that is not an image a browser can read.' };
        }
        let encoded;
        try {
          encoded = await encodeBackground(raw, kind);
        } catch (e) {
          return { ok: false, message: e instanceof Error ? e.message : String(e) };
        }
        background = {
          bytes: encoded.bytes,
          mediaType: encoded.mediaType,
          fit: current.background?.fit ?? 'cover',
          dimPercent: current.background?.dimPercent ?? 0,
          // Provenance travels with the picture. A generated image behind live process data should
          // be answerable months later, and "an AI made it" is not an answer.
          prompt: current.background?.prompt ?? '',
          model: current.background?.model ?? '',
        };
      }

      if (background) {
        const fit = String(args.fit ?? '');
        if (fit === 'cover' || fit === 'contain' || fit === 'tile') background = { ...background, fit };
        if (args.dim !== undefined) {
          const dim = Number(args.dim);
          // Clamped rather than refused: a slider that went too far should not lose the picture.
          background = { ...background, dimPercent: Number.isFinite(dim) ? Math.max(0, Math.min(90, Math.round(dim))) : background.dimPercent };
        }
      }

      const board: Dashboard = {
        id,
        title: current.title,
        description: current.description,
        digest: summary.digest,
        updatedAtMs: Date.now(),
        tileCount: current.tiles.length,
        tiles: current.tiles,
        ...(background ? { background } : {}),
      };
      const r = await entry.dashboards.save(board, summary.digest, 'AI assistant');
      if (!r.ok) return { ok: false, message: `The relay refused that write: ${r.error}` };
      if (!background) {
        return { ok: true, message: `Removed the background from "${current.title}".`, data: { dashboardId: id, background: false } };
      }
      return {
        ok: true,
        message:
          `Set the background on "${current.title}" — ${Math.round(background.bytes.byteLength / 1024)} KB, ` +
          `${background.fit}, dimmed ${background.dimPercent}%. You cannot see it; ask the person whether it reads well behind the tiles.`,
        data: { dashboardId: id, background: true, bytes: background.bytes.byteLength, fit: background.fit, dimPercent: background.dimPercent },
      };
    },
  };

  /*
   * RUN IT BEFORE ANYONE PLACES IT.
   *
   * A widget is written in one place and judged in another: what a model cannot see from its own
   * source is that the text it drew falls outside a narrow tile, that a colour it chose is one the
   * renderer drops, or that it prints NaN before the first reading arrives. Told those rules, a model
   * follows most of them; shown what its own widget did, it fixes the rest. So the rules are
   * available as an ACT rather than only as instructions — the same run the board would give it, at
   * three tile sizes, with a reading and without.
   *
   * A CLIENT tool for the same reason the rest of this file is: running a widget needs the sandbox
   * frame, which is this browser's, and a worker cannot compile code at all.
   */
  const check: ClientTool = {
    name: 'widgets.check',
    /*
     * The AUTHOR'S OWN key, not an open door.
     *
     * Running a widget reaches no plant data — it draws synthetic readings in a sandbox — so the
     * instinct is to grant it to everyone. But a stored tool subset is a floor as well as a ceiling:
     * a tool every role resolves is one every specialist's roster has to ship, and the analyst and
     * the scxml author have no widget to run. It rides the key that already means "this one writes
     * widgets", so the roster that offers it is exactly the roster that can use it.
     */
    permission: Permission.WIDGET_AUTHOR,
    description:
      'Run a widget source the way a board would — at three tile sizes, once with a reading and once with none — and report what the runtime saw: whether it started, whether it drew anything, and every way its drawing broke the rules (a colour that is not a token, NaN on screen, text outside the tile). Call it on a widget you just wrote, BEFORE returning it or placing it, and fix what it reports. Takes a few seconds.',
    parameters: {
      type: 'object',
      properties: {
        source: { type: 'string', description: 'The complete widget module body, exactly as it would run.' },
        config: { type: 'object', description: 'The settings a tile would pass it — e.g. controllerId/symbolId, or a modelId for a widget that reads a model. Every source the widget subscribes gets a reading synthesised in the check, so a model-reading or multi-source widget draws too, not only a single top-level symbolId.', additionalProperties: true },
      },
      required: ['source'],
      additionalProperties: false,
    },
    run: async (args) => {
      const a = args as { source?: unknown; config?: unknown };
      const source = typeof a.source === 'string' ? a.source.trim() : '';
      if (!source) return { ok: false, message: 'Pass the widget source to check.' };
      const config = a.config && typeof a.config === 'object' && !Array.isArray(a.config) ? (a.config as Record<string, unknown>) : {};
      if (!deps.checkWidget) return { ok: false, message: 'This surface cannot run a widget, so it cannot check one.' };
      const r = await deps.checkWidget(source, config);
      if (r.ok) return { ok: true, message: 'The widget ran at every size, with a reading and without, and broke none of the rules.', data: r };
      // A refusal a model can act on: what happened, in the order it has to be fixed in.
      const why = !r.started
        ? `It never started${r.fault ? `: ${r.fault}` : ' — setup() did not return a render function, or the module threw.'}`
        : !r.painted
          ? `It started but drew nothing${r.fault ? `: ${r.fault}` : '.'}`
          : r.fault
            ? `It stopped: ${r.fault}`
            : `It drew, but broke the rules: ${r.problems.join(' | ')}`;
      return { ok: false, message: why, data: r };
    },
  };

  return registerClientTools(widgets, symbols, list, read, create, update, del, setBackground, check);
}
